import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Send, Plus, MessageSquare, Languages, Sparkles, CheckCircle2, BookOpen, Trash2, Lock, ShieldCheck, Scale, Calendar, ArrowRight } from "lucide-react";
import Header from "@user/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SUGGESTIONS = [
  "My landlord is asking me to vacate",
  "How to file FIR?",
  "Divorce process in India",
  "Property dispute help",
];

export default function Chat() {
  const { id: paramId } = useParams();
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [currentId, setCurrentId] = useState(paramId || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("english");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadChats = async () => {
    try {
      const res = await api.get("/chats");
      setChats(res.data);
    } catch {}
  };

  const loadChat = async (id) => {
    try {
      const res = await api.get(`/chats/${id}`);
      setMessages(res.data.messages || []);
      setCurrentId(id);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    if (!user || loading) return;
    loadChats();
  }, [user, loading]);

  useEffect(() => {
    if (paramId && user) loadChat(paramId);
  }, [paramId, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // "+ New chat" is a pure client-side reset — no empty chat persisted.
  // Backend chat is created on-demand inside send() when the first message is sent.
  const startNewChat = () => {
    setCurrentId(null);
    setMessages([]);
    setInput("");
    if (paramId) navigate("/chat");
  };

  const createChat = async () => {
    const res = await api.post("/chats", {});
    setChats([res.data, ...chats]);
    setCurrentId(res.data.id);
    setMessages([]);
    navigate(`/chat/${res.data.id}`);
    return res.data.id;
  };

  const deleteChat = async (id) => {
    await api.delete(`/chats/${id}`);
    setChats((x) => x.filter((c) => c.id !== id));
    if (currentId === id) {
      setCurrentId(null);
      setMessages([]);
      navigate("/chat");
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;
    if (!user) {
      toast.info("Please sign in to save and send messages");
      login();
      return;
    }
    setSending(true);
    let chatId = currentId;
    try {
      if (!chatId) chatId = await createChat();
      setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
      setInput("");
      const res = await api.post(`/chats/${chatId}/messages`, { content: text, language });
      setMessages((m) => {
        const filtered = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
      // refresh chat title in sidebar
      loadChats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#1E3A8A] animate-spin" /></div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-[#DBEAFE] text-[#1E3A8A] grid place-items-center">
            <Lock size={24} />
          </div>
          <h1 className="mt-5 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Sign in to ask a legal question</h1>
          <p className="mt-3 text-slate-600">Your chats are saved privately and securely to your account.</p>
          <Button
            onClick={login}
            data-testid="chat-signin-btn"
            className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-7 font-semibold"
          >
            Sign in / Sign up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-3 h-fit" data-testid="chat-sidebar">
          <Button
            onClick={startNewChat}
            data-testid="new-chat-btn"
            className="w-full rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white justify-start gap-2"
          >
            <Plus size={16} /> New chat
          </Button>
          <div className="mt-3 space-y-1 max-h-[60vh] overflow-y-auto adv-scroll-hide">
            {chats.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No chats yet</p>}
            {chats.map((c) => (
              <div
                key={c.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${currentId === c.id ? "bg-blue-50 text-[#1E3A8A]" : "hover:bg-slate-50 text-slate-700"}`}
                onClick={() => navigate(`/chat/${c.id}`)}
                data-testid={`chat-item-${c.id}`}
              >
                <MessageSquare size={14} />
                <span className="flex-1 truncate font-medium">{c.title}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                  data-testid={`delete-chat-${c.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Main chat */}
        <section className="lg:col-span-9 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden min-h-[70vh]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#1E3A8A] text-white grid place-items-center"><Sparkles size={16} /></div>
              <div>
                <p className="text-sm font-bold text-slate-900">Advoset AI</p>
                <p className="text-xs text-slate-500">Plain-language legal help</p>
              </div>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="lang-select">
                <Languages size={14} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AI disclaimer banner */}
          <div
            data-testid="ai-disclaimer-banner"
            className="flex items-start gap-2 px-5 py-2.5 bg-amber-50/70 border-b border-amber-100 text-[11px] text-amber-900"
          >
            <ShieldCheck size={14} className="shrink-0 mt-0.5 text-amber-600" />
            <span>
              <span className="font-bold">AI-generated guidance.</span>{" "}
              For accurate legal advice,{" "}
              <Link to="/lawyers" className="underline font-semibold hover:text-amber-950">consult a verified lawyer</Link>.
            </span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="messages-container">
            {messages.length === 0 && (
              <div className="max-w-xl mx-auto text-center py-12">
                <div className="h-12 w-12 mx-auto rounded-2xl bg-[#DBEAFE] text-[#1E3A8A] grid place-items-center">
                  <Sparkles size={22} />
                </div>
                <h2 className="mt-4 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>How can I help?</h2>
                <p className="mt-2 text-sm text-slate-600">Ask anything about Indian law. Try one of these:</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`suggestion-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                      className="text-left px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-[#1E3A8A] hover:bg-blue-50/50 text-sm text-slate-700 transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "user" ? (
                  <div className="max-w-[85%] bg-blue-50 text-slate-900 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                    {m.content}
                  </div>
                ) : (
                  <div className="max-w-[92%] bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                    {m.summary ? (
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase flex items-center gap-1">
                            <BookOpen size={10} /> Summary
                          </p>
                          <p className="text-sm text-slate-800 mt-1">{m.summary}</p>
                        </div>
                        {m.explanation && (
                          <div>
                            <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase">Legal Explanation</p>
                            <p className="text-sm text-slate-800 mt-1 leading-relaxed whitespace-pre-wrap">{m.explanation}</p>
                          </div>
                        )}
                        {m.next_steps?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase">Next Steps</p>
                            <ul className="mt-1 space-y-1.5">
                              {m.next_steps.map((s, i) => (
                                <li key={i} className="flex gap-2 text-sm text-slate-800">
                                  <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2" data-testid="typing-indicator">
                  <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-[#1E3A8A] animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}

            {/* Conversion prompt after 1-2 AI responses — nudge to paid consultation */}
            {!sending && messages.filter((m) => m.role === "assistant").length >= 1 && (
              <div
                data-testid="lawyer-conversion-prompt"
                className="relative rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#FBF4DF] to-white p-4 md:p-5 mt-4"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl adv-navy grid place-items-center shrink-0">
                    <Scale size={18} className="text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                      Want expert advice from a verified lawyer?
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Real lawyers, real answers — matched to your city & language.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        asChild
                        data-testid="prompt-chat-with-lawyer"
                        className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
                      >
                        <Link to="/lawyers?type=chat">
                          <MessageSquare size={13} /> Chat with Lawyer
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        data-testid="prompt-book-consultation"
                        className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold text-xs h-9 px-4"
                      >
                        <Link to="/lawyers">
                          <Calendar size={13} /> Book Consultation <ArrowRight size={13} />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 md:p-4 bg-white">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder={language === "hindi" ? "अपना सवाल यहाँ लिखें…" : "Type your legal question…"}
                data-testid="chat-input"
                rows={1}
                className="min-h-[44px] max-h-[140px] resize-none rounded-xl"
              />
              <Button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="send-btn"
                className="rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11 w-11 p-0 shrink-0"
              >
                <Send size={16} />
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Advoset provides general information, not legal advice. Always consult a qualified lawyer for your case.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
